/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { FormFeedback, Label, Input, Button } from "reactstrap";
import MoreInfoIcon from "../../elements/MoreInfoIcon";
import debug from "../../util/constants/Debug";

export const getId = ({path, name}) => {
  if (path != null && name != null) {
    return `${path}.${name}`;
  }
  if (path != null) {
    return path;
  }
  if (name != null) {
    return name;
  }
  return null;
};
export const iterateId = (obj, id) => id.split(".").reduce((acc, key) => acc?.[key], obj);

export const RequiredIndication = ({show = true}) => show ? (
  <span className="ps-2" style={{ color: "red" }}>*</span>
) : null;

const FormInput = ({
  inputId,
  path,
  name,
  text,
  placeholder,
  help,
  type,
  value,
  accept,
  maxChars,
  size,
  tooltip,
  clearable,
  defaultValue,
  defaultChecked,
  disabled,
  required,
  reqInd,
  validate,
  onChange,
  onBlur,
  register,
  control,
  watch,
  setValue,
  errors,
  style,
  hideError = false,
  className,
  children
}) => {
  // constants
  const maxCharacters = maxChars ?? 50;
  const isDisabled = disabled ?? false;

  // register input
  const id = getId({path, name});
  const { ref, ...registration } = register(id, {
    ...(required) && { required: true },
    ...(validate) && { validate: validate },
    ...(onChange) && { onChange: onChange },
    ...(onBlur)   && { onBlur: onBlur },
  });

  // separate key path
  const error = iterateId(errors, id);

  // decide if invalid
  const invalidFeedback = error?.message;
  const invalid = !disabled && invalidFeedback != null;
  const indicateRequired = reqInd ?? required ?? false;

  // handlers
  const handleClear = () => {
    if (!setValue) {
      debug.error("Clearable field requires setValue not null");
      return;
    }
    setValue(id, null);
  };

  // return
  if (type === "checkbox" || type === "radio") {
    const isDefaultChecked = type === "checkbox"
      ? (watch?.(id) ?? defaultChecked ?? false)
      : (defaultChecked ?? false);
      
    return (
      <div className={className}>
        <Input
          id={inputId}
          innerRef={ref}
          className="me-2"
          type={type}
          value={value}
          invalid={invalid}
          disabled={isDisabled}
          defaultChecked={isDefaultChecked}
          control={control}
          style={style}
          {...registration}
        />
        {text && <Label check>{text}</Label>}
      </div>
    );
  }

  const inputError = () => {
    if (hideError || !invalid) {
      return null;
    }
    return (
      <FormFeedback>
        {required && !invalidFeedback ? "Field Required" : invalidFeedback}
      </FormFeedback>
    );
  };

  const inputProps = {
    id: inputId,
    innerRef: ref,
    placeholder: placeholder,
    type: type ?? "text",
    inputMode: type === "numeric" ? "numeric" : null,
    bsSize: size,
    accept: accept,
    defaultValue: defaultValue,
    maxLength: maxCharacters,
    invalid: invalid,
    disabled: isDisabled,
    style: style,
    ...registration
  };

  return (
    <div className={className}>
      {text && (
        <Label className="mt-1" for={name}>
          {text}
          {tooltip && <>{' '}<MoreInfoIcon>{tooltip}</MoreInfoIcon></>}
          <RequiredIndication show={indicateRequired} />
        </Label>
      )}
      {help != null && help}
      <div className="d-flex">
        {clearable && (
          <Button
            className="me-2"
            outline
            color="danger"
            size="sm"
            onClick={handleClear}
          >
            <i className="icon bi bi-trash3" />
          </Button>
        )}
        <div className="flex-dynamic" style={{ overflow: "visible" }}>
          {type === "select-edit" ? (
            <div className="select-edit">
              <div style={{ position: "relative" }}>
                <Input {...inputProps} className="select-edit-input" type="text" />
              </div>
              <Input
                className="select-edit-select"
                type="select"
                invalid={invalid}
                tabIndex="-1"
                value="PLACEHOLDER-VALUE"
                onChange={(e) => setValue(id, e.target.value)}
              >
                <option hidden value="PLACEHOLDER-VALUE" />
                {children}
              </Input>
              {inputError()}
            </div>
          ) : (
            <>
              <Input {...inputProps}>
                {children}
              </Input>
              {inputError()}
            </>
          )}
        </div>
        <RequiredIndication show={!text && indicateRequired} />
      </div>
    </div>
  );
};

export default FormInput;